import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import {
  fetchNoteQuestions,
  fetchAddProcessNoteStructured,
  getNoteQuestions,
  getNoteQuestionsLoading,
} from "store/slices/processNote";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  header: {
    borderBottom: "1px solid #E5E7EB",
    paddingBottom: 12,
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
  },
  headerMeta: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 3,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  shiftSelect: {
    padding: "4px 10px",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    fontSize: 12,
    color: "#374151",
    background: "#fff",
    cursor: "pointer",
  },
  sectionCount: {
    fontSize: 12,
    color: AppColor.mainColor,
    fontWeight: 600,
    border: `1px solid ${AppColor.mainColor}`,
    borderRadius: 20,
    padding: "3px 10px",
  },

  // Context hint banner
  contextBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    background: "#EFF6FF",
    border: "1px solid #BFDBFE",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 16,
    fontSize: 13,
    color: "#1D4ED8",
    lineHeight: 1.5,
  },
  contextIcon: {
    marginTop: 1,
    flexShrink: 0,
    color: "#3B82F6",
  },

  // Section card
  sectionCard: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    marginBottom: 14,
    overflow: "hidden",
  },
  sectionCardActive: {
    borderColor: AppColor.mainColor,
  },
  sectionCardError: {
    borderColor: "#EF4444",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    background: "#FAFAFA",
    borderBottom: "1px solid #F3F4F6",
  },
  sectionNumber: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: AppColor.mainColor,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
  },
  sectionNumberDone: {
    background: "#10B981",
  },
  sectionQuestion: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    flex: 1,
  },
  sectionBody: {
    padding: "14px 16px",
    background: "#fff",
  },

  // Goal badge
  goalBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#ECFDF5",
    border: "1px solid #A7F3D0",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 11,
    color: "#065F46",
    fontWeight: 500,
    marginBottom: 12,
  },

  // Quick-answer chips
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  chip: {
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    border: "1px solid #D1D5DB",
    background: "#fff",
    color: "#374151",
    transition: "all 0.15s",
    "&:hover": {
      borderColor: AppColor.mainColor,
      color: AppColor.mainColor,
    },
  },
  chipSelected: {
    background: AppColor.mainColor,
    color: "#fff",
    border: `1px solid ${AppColor.mainColor}`,
  },

  // Detail textarea
  detailArea: {
    width: "100%",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    color: "#374151",
    resize: "vertical",
    minHeight: 70,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    "&:focus": {
      borderColor: AppColor.mainColor,
    },
  },

  // Error
  errorMsg: {
    fontSize: 11,
    color: "#EF4444",
    marginTop: 4,
  },

  // Handover section
  handoverCard: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    marginBottom: 14,
    overflow: "hidden",
  },
  handoverHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    background: "#FAFAFA",
    borderBottom: "1px solid #F3F4F6",
  },
  handoverBody: {
    padding: "14px 16px",
    background: "#fff",
  },
  handoverHint: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 6,
  },
  handoverArea: {
    width: "100%",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    color: "#374151",
    resize: "vertical",
    minHeight: 90,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    "&:focus": {
      borderColor: AppColor.mainColor,
    },
  },

  // Footer
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    paddingTop: 12,
    borderTop: "1px solid #F3F4F6",
    marginTop: 4,
  },

  loadingBox: {
    padding: "40px 0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    color: "#6B7280",
    fontSize: 13,
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseQuickAnswers = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((s) => String(s).trim()).filter(Boolean);
    } catch (_) { /* not JSON — fall through to CSV split */ }
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const MIN_HANDOVER_WORDS = 20;

const countWords = (text) =>
  text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

// ─── Component ────────────────────────────────────────────────────────────────

const EndOfShiftNoteModal = ({
  open,
  onClose,
  jobId,
  clientId,
  bookingId,
  participantName,
  workerName,
  shiftDate,
  shiftLabel,
  onSuccess,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const questions = useSelector(getNoteQuestions);
  const questionsLoading = useSelector(getNoteQuestionsLoading);

  // answers: { [questionId]: { answer: string, detail: string } }
  const [answers, setAnswers] = useState({});
  const [handover, setHandover] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setAnswers({});
      setHandover("");
      setErrors({});
      // Fetch questions via clientId, jobId or bookingId
      dispatch(fetchNoteQuestions({
        ...(clientId && { clientId }),
        ...(jobId && { jobId }),
        ...(bookingId && { bookingId })
      }));
    }
  }, [open, clientId, jobId, bookingId, dispatch]);

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = useCallback(() => {
    const errs = {};

    questions.forEach((q) => {
      const ans = answers[q.id];
      if (!ans?.answer) {
        errs[`q_${q.id}`] = "Please select an answer.";
      }
    });

    const wordCount = countWords(handover);
    if (wordCount < MIN_HANDOVER_WORDS) {
      errs.handover = `Min ${MIN_HANDOVER_WORDS} words · ${wordCount} written`;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [questions, answers, handover]);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const answerList = questions.map((q) => ({
      questionId: q.id,
      answer: answers[q.id]?.answer || "",
      detail: answers[q.id]?.detail || null,
    }));

    const result = await dispatch(
      fetchAddProcessNoteStructured({ jobId, clientId, handover, answers: answerList })
    );
    setSubmitting(false);

    if (fetchAddProcessNoteStructured.fulfilled.match(result)) {
      onSuccess && onSuccess();
      onClose();
    }
  };

  // ── Quick-answer select ───────────────────────────────────────────────────

  const handleSelectAnswer = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], answer },
    }));
    if (errors[`q_${questionId}`]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`q_${questionId}`];
        return next;
      });
    }
  };

  const handleDetailChange = (questionId, detail) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], detail },
    }));
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const completedCount = questions.filter((q) => answers[q.id]?.answer).length;
  const totalSections = questions.length + 1; // +1 for handover
  const handoverDone = countWords(handover) >= MIN_HANDOVER_WORDS;
  const completedTotal = completedCount + (handoverDone ? 1 : 0);

  // ── Render ────────────────────────────────────────────────────────────────

  const renderSection = (q, index) => {
    const chips = parseQuickAnswers(q.quickAnswers);
    const selected = answers[q.id]?.answer;
    const detail = answers[q.id]?.detail || "";
    const isDone = Boolean(selected);
    const hasError = Boolean(errors[`q_${q.id}`]);

    return (
      <Box
        key={q.id}
        className={`${classes.sectionCard} ${isDone ? classes.sectionCardActive : ""} ${hasError ? classes.sectionCardError : ""}`}
      >
        <Box className={classes.sectionHeader}>
          <Box className={`${classes.sectionNumber} ${isDone ? classes.sectionNumberDone : ""}`}>
            {isDone ? "✓" : index + 1}
          </Box>
          <Box className={classes.sectionQuestion}>{q.question}</Box>
        </Box>

        <Box className={classes.sectionBody}>
          {/* Goal-linked badge */}
          {q.isGoalLinked && q.goalLabel && (
            <Box className={classes.goalBadge}>
              <i className="fas fa-chart-line" style={{ fontSize: 10 }} />
              {q.goalLabel}
            </Box>
          )}

          {/* Quick-answer chips */}
          {chips.length > 0 && (
            <Box className={classes.chipsRow}>
              {chips.map((chip) => (
                <span
                  key={chip}
                  className={`${classes.chip} ${selected === chip ? classes.chipSelected : ""}`}
                  onClick={() => handleSelectAnswer(q.id, chip)}
                >
                  {chip}
                </span>
              ))}
            </Box>
          )}

          {/* Detail textarea (shown when hasDetail is true) */}
          {q.hasDetail && (
            <textarea
              className={classes.detailArea}
              placeholder={q.detailPlaceholder || "Add detail…"}
              value={detail}
              onChange={(e) => handleDetailChange(q.id, e.target.value)}
              rows={3}
            />
          )}

          {hasError && (
            <Box className={classes.errorMsg}>{errors[`q_${q.id}`]}</Box>
          )}
        </Box>
      </Box>
    );
  };

  const wordCount = countWords(handover);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title=""
      maxWidth="md"
    >
      {/* Custom header inside dialog */}
      <Box className={classes.header}>
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Box className={classes.headerTitle}>
              End-of-shift note{participantName ? ` — ${participantName}` : ""}
            </Box>
            <Box className={classes.headerMeta}>
              {[shiftLabel, shiftDate, workerName].filter(Boolean).join(" · ")}
            </Box>
          </Box>
          {totalSections > 0 && (
            <Box className={classes.sectionCount}>
              {completedTotal} of {totalSections} sections
            </Box>
          )}
        </Box>
      </Box>

      {questionsLoading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress size={18} style={{ color: AppColor.mainColor }} />
          Loading note sections…
        </Box>
      ) : (
        <>
          {/* Sections */}
          {questions.map((q, i) => (
            <React.Fragment key={q.id}>
              {/* Context hint banner just above the section it belongs to */}
              {q.contextHint && (
                <Box className={classes.contextBanner}>
                  <i className="fas fa-sparkles" style={{ fontSize: 13, marginTop: 1 }} />
                  {q.contextHint}
                </Box>
              )}
              {renderSection(q, i)}
            </React.Fragment>
          ))}

          {/* Handover section (always last) */}
          <Box
            className={`${classes.handoverCard} ${handoverDone ? classes.sectionCardActive : ""} ${errors.handover && !handoverDone ? classes.sectionCardError : ""}`}
          >
            <Box className={classes.handoverHeader}>
              <Box className={`${classes.sectionNumber} ${handoverDone ? classes.sectionNumberDone : ""}`}>
                {handoverDone ? "✓" : questions.length + 1}
              </Box>
              <Box className={classes.sectionQuestion}>
                Handover — what does the next worker need to know?
              </Box>
            </Box>
            <Box className={classes.handoverBody}>
              <textarea
                className={classes.handoverArea}
                placeholder="Medication timing, appointments, mood, anything unusual…"
                value={handover}
                onChange={(e) => {
                  setHandover(e.target.value);
                  if (errors.handover) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.handover;
                      return next;
                    });
                  }
                }}
                rows={4}
              />
              <Box className={classes.handoverHint}>
                Min {MIN_HANDOVER_WORDS} words · Visible to next worker in mobile app before shift starts
                {wordCount > 0 && (
                  <span style={{ marginLeft: 8, color: wordCount >= MIN_HANDOVER_WORDS ? "#10B981" : "#EF4444" }}>
                    ({wordCount} words)
                  </span>
                )}
              </Box>
              {errors.handover && (
                <Box className={classes.errorMsg}>{errors.handover}</Box>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* Footer actions */}
      <Box className={classes.footer}>
        <AppButton
          label="Cancel"
          onClick={onClose}
          buttonColor="#F3F4F6"
          rounder
          style={{ color: "#111827" }}
          disabled={submitting}
        />
        <AppButton
          label={submitting ? "Submitting…" : "Submit note"}
          rounder
          onClick={handleSubmit}
          isLoading={submitting}
          disabled={submitting || questionsLoading}
          buttonColor={AppColor.mainColor}
        />
      </Box>
    </AppDialog>
  );
};

EndOfShiftNoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  jobId: PropTypes.number,
  clientId: PropTypes.number,
  bookingId: PropTypes.number,
  participantName: PropTypes.string,
  workerName: PropTypes.string,
  shiftDate: PropTypes.string,
  shiftLabel: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default EndOfShiftNoteModal;
